/**
 * Site Context — claim extractor (Site_Context SC3).
 *
 * Runs a short Gemini Flash call against the user's freeform
 * site-context prose and returns a typed `ExtractedClaim[]`. Flash
 * (NOT Pro) is intentional — claim extraction is bounded, structured,
 * and runs on every keystroke-debounced blur. Pro would be over-budget;
 * Flash returns in ~1-2s at ~$0.001 / call.
 *
 * Contract:
 *   - Best-effort: any failure (network, schema, parse) returns an
 *     empty result and a stamped extractor version so the caller can
 *     persist the raw prose without blocking.
 *   - Input is hard-capped at SITE_CONTEXT_MAX_CHARS (8000) by the
 *     store boundary; we re-clip here as belt-and-braces.
 *   - All requests route through the shared `ai` client so the
 *     /api/gemini-proxy auth patch is in place.
 */
import { Type, type Schema } from '@google/genai';
import type { ReportTypeId } from '../../config/reportTypes';
import type { ClaimCategory, ExtractedClaim } from '../../types/siteContext';
import { SITE_CONTEXT_MAX_CHARS } from '../../types/siteContext';
import { ai } from '../geminiClient';
import { GEMINI_FLASH_MODEL } from '../../config/geminiModels';
// SCE-2 — schema-driven post-validation. Every accepted LLM claim runs
// through `validateClaim` before it lands in `result.claims`; rejected
// claims surface in `result.rejected[]` for the admin telemetry rollup
// and the SCE-FOLLOWUP-AUTO-RE-EXTRACT path. Schema lives in
// `src/lib/sed/fieldSchema.ts`.
import type {
  SceQualityWarning,
  SceRejectionCode,
} from '../../lib/sed/fieldSchema';
import { validateClaim, sourceSpanContainsAnyToken } from '../../lib/sed/fieldSchema';
// EX-6 — negation-polarity detector now lives in a shared module so the
// DOC channel can reuse it. Imported for internal use (boolean branch)
// and re-exported below to preserve this file's public surface + the
// regression suite's `import { detectNegationPolarity }` from here.
import { detectNegationPolarity } from '../../lib/sed/negationPolarity';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
// CL-1 (row 0.99286) — CSE confidence calibration multiplier table. All
// 32 multipliers default to 1.0 so this is a no-op at ship time; live-
// prod telemetry can shift specific claim types via the calibration
// table without changing the rest of the pipeline.
import { calibrateClaimConfidence } from './cseCalibration';
import { estimateTokens } from '../../lib/evidenceLineage/requirementLineage';
import { retryBackoffMs, sleep } from '../../lib/httpRetry';

export interface ExtractClaimsOptions {
  siteName?: string;
  reportType?: ReportTypeId;
  /**
   * SCE-2 — live SED (or the report's SED snapshot) used by the
   * cross-field rules. Pass `null` / omit to disable cross-field
   * checks; the type + unit + range + enum rules still apply.
   */
  sed?: SiteEvidenceDocument | null;
  /**
   * SCE-2 — two-letter USPS code for the site, used by the
   * `regulatory.state` locus rule. Resolved by the caller via
   * `inferSiteStateFromSed` or the address parser. Pass `null` /
   * `undefined` to disable the cross-state guard (legacy behaviour
   * pre-SCE).
   */
  siteStateCode?: string | null;
  /**
   * SCE-FOLLOWUP-AUTO-RE-EXTRACT-ON-MISMATCH (SX-1). Prepended to the
   * prompt body when the orchestrator (`extractClaimsWithRetry`)
   * fires a SECOND extraction after the first pass produced rejected
   * claims. Captures the rejected proposals + their failure reasons
   * so the LLM gets explicit do-NOT-repeat guidance.
   *
   * Callers SHOULD NOT set this themselves — the field is part of the
   * extractor's retry contract. Leaving it `undefined` (the default)
   * reproduces the pre-SX-1 extractor behaviour exactly.
   */
  retryContext?: SceRetryContext;
}

/**
 * SCE-FOLLOWUP-AUTO-RE-EXTRACT-ON-MISMATCH (SX-1) — context the retry
 * pass receives. The orchestrator builds this from the FIRST pass's
 * `rejected[]` and passes it back into a single re-invocation of
 * `extractClaims`. Mirrors MR-1's "strengthened-prompt-prefix" shape.
 */
export interface SceRetryContext {
  /** Truthy marker so prompt builders can branch cheaply. */
  isRetry: true;
  /** Rejected claims from the prior pass, with their failure reasons. */
  priorRejections: ReadonlyArray<Pick<SceRejectedClaim, 'proposedSedField' | 'proposedValue' | 'sourceSpan' | 'reasons'>>;
}

/**
 * CLP-1 — discriminated status the extractor emits so the UI can
 * distinguish "never-ran" / "ran-and-failed-silently" / "ran-and-
 * genuinely-found-nothing" / "ran-and-quota-429'd" / etc. Before CLP,
 * every error path returned `{ claims: [], extractorVersion }` with
 * `console.warn` only; the chip just read "0 claims parsed" whether
 * extraction never ran, errored, or genuinely found nothing.
 *
 * Status semantics:
 *   - `'idle'`         ⇒ no extraction has run yet (default for fresh contexts).
 *   - `'extracting'`   ⇒ extraction in flight (reserved; the extractor
 *                        itself never returns this, callers may set it
 *                        on optimistic in-flight UI updates).
 *   - `'success'`      ⇒ extraction completed (claims may still be
 *                        empty when the prose genuinely has none).
 *   - `'quota-error'`  ⇒ Gemini 429 / RESOURCE_EXHAUSTED / spending cap.
 *   - `'auth-error'`   ⇒ 401 / 403 / invalid-key.
 *   - `'timeout'`      ⇒ extractor timeout budget exceeded.
 *   - `'parse-error'`  ⇒ response wasn't valid JSON for the schema.
 *   - `'unknown-error'`⇒ anything else (network, internal).
 */
export type ExtractorStatus =
  | 'idle'
  | 'extracting'
  | 'success'
  | 'auth-error'
  | 'quota-error'
  | 'timeout'
  | 'parse-error'
  | 'unknown-error';

/**
 * SCE-2 — one entry in the `rejected[]` array. The extractor refused to
 * promote this LLM output into `claims[]` because the post-validation
 * gate (schema type / unit token / range / cross-field) failed. Audit
 * trail for admin telemetry + the SCE-FOLLOWUP-AUTO-RE-EXTRACT loop.
 */
export interface SceRejectedClaim {
  /** Category the LLM proposed (may be any string — we did not require
   *  ClaimCategory membership for rejected rows so the admin panel can
   *  bucket truly novel proposals too). */
  proposedCategory: string;
  /** SED dot-path the LLM proposed. Absent when the LLM emitted nothing
   *  (i.e. the rejection happened on category / confidence / etc.). */
  proposedSedField?: string;
  /** The raw coerced value the LLM emitted. */
  proposedValue: string | number | boolean | undefined;
  /** Verbatim slice from the narrative. */
  sourceSpan: string;
  /** Why we refused. Always at least one entry. */
  reasons: Array<{ code: SceRejectionCode; reason: string }>;
  /** Bumped each time the prompt body / schema changes — caller can
   *  decide whether to retry on version bump. */
  extractorVersion: string;
}

export interface ExtractClaimsResult {
  claims: ExtractedClaim[];
  /** Version stamp persisted on `SiteContext.extractorVersion`. */
  extractorVersion: string;
  /** CLP-1 — classified outcome of the run; drives the chip + retry UX. */
  status: ExtractorStatus;
  /** Human-readable error string when `status !== 'success'`. */
  errorMessage?: string;
  /** Epoch ms of when the extractor was invoked. */
  attemptedAt: number;
  /**
   * SCE-2 — claims the LLM proposed but the schema-driven post-validator
   * refused. Empty array when nothing was rejected. Feeds the admin
   * "Extractor rejections" rollup (SCE-5) and the per-site quality chip.
   * Optional for back-compat with callers that destructure only
   * `{ claims, extractorVersion }` — undefined ⇒ empty.
   */
  rejected?: SceRejectedClaim[];
}

/**
 * CLP-1 — classify a thrown error into a status bucket. Kept exported
 * so the rerun panel (which calls extractClaims directly) can share the
 * same classifier without re-implementing the regexen.
 */
export function classifyExtractorError(err: unknown): ExtractorStatus {
  const raw = err instanceof Error ? err.message : String(err);
  const msg = String(raw ?? '');
  if (/429|RESOURCE_EXHAUSTED|spending cap|quota/i.test(msg)) return 'quota-error';
  if (/\b401\b|\b403\b|unauthori[sz]ed|invalid.+key|api key/i.test(msg)) return 'auth-error';
  if (/timeout|timed out|abort/i.test(msg)) return 'timeout';
  if (/parse|json|syntax/i.test(msg)) return 'parse-error';
  return 'unknown-error';
}

/**
 * Bumped when the prompt body or schema changes — drives re-extract on
 * the next save. SCE-2 (2026-05-18) bumps from v1 → v2 to invalidate
 * persisted SiteContexts whose claims were extracted before the
 * typed-field schema (`src/lib/sed/fieldSchema.ts`) post-validated
 * every claim. The bump alone is the invalidation story; the lazy
 * read-time validator in SCE-4 stamps `qualityWarnings` on any legacy
 * claim that would have been rejected today.
 */
export const EXTRACTOR_VERSION = 'gemini-flash-2026.05.18-v2-sce';

/** Use the configured Flash tier, including its existing runtime override. */
const FLASH_MODEL = GEMINI_FLASH_MODEL;

/**
 * Timeout budget for the extractor call (client-side only — the proxy
 * allows up to 8 min). SC3 runs on interactive saves; we scale with input
 * size because structured JSON on 6–8k prose + the static prompt tail
 * routinely exceeds 40s through Vertex + browser proxy.
 */
const FLASH_TIMEOUT_BASE_MS = 45_000;
const FLASH_TIMEOUT_MAX_MS = 90_000;
const FLASH_TIMEOUT_PER_1K_CHARS_MS = 8_000;

/** At most one automatic retry after a client-side timeout. */
const EXTRACTOR_TIMEOUT_MAX_ATTEMPTS = 2;

/**
 * Wall-clock budget for a single `generateContent` race. Exported for
 * tests; production callers should use `extractClaims` only.
 */
export function computeExtractorTimeoutMs(charCount: number): number {
  const extra = Math.max(0, Math.ceil(charCount / 1000) - 1) * FLASH_TIMEOUT_PER_1K_CHARS_MS;
  return Math.min(FLASH_TIMEOUT_MAX_MS, FLASH_TIMEOUT_BASE_MS + extra);
}

function logExtractClaimsFailure(
  status: ExtractorStatus,
  msg: string,
  diag: {
    inputText: string;
    prompt: string;
    timeoutMs: number;
    attempt: number;
  },
): void {
  if (status !== 'timeout') {
    console.warn(`[site-context] extractClaims failed (${status}):`, msg);
    return;
  }
  console.warn('[site-context] extractClaims failed (timeout):', msg, {
    inputChars: diag.inputText.length,
    promptChars: diag.prompt.length,
    estimatedInputTokens: estimateTokens(diag.inputText),
    estimatedPromptTokens: estimateTokens(diag.prompt),
    timeoutMs: diag.timeoutMs,
    attempt: diag.attempt,
    maxAttempts: EXTRACTOR_TIMEOUT_MAX_ATTEMPTS,
  });
}

async function generateClaimsWithTimeout(
  prompt: string,
  timeoutMs: number,
): Promise<{ text?: string }> {
  // Bound the underlying SDK request as well as the caller's wait. A race
  // alone leaves its request live while the automatic retry starts.
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error(`extractClaims timed out after ${timeoutMs}ms`);
        reject(error);
        controller.abort(error);
      }, timeoutMs);
    });
    const responsePromise = ai.models.generateContent({
      model: FLASH_MODEL,
      contents: prompt,
      config: {
        responseMimeType: 'application/json',
        responseSchema: RESPONSE_SCHEMA,
        temperature: 0.1,
        httpOptions: { timeout: timeoutMs },
        abortSignal: controller.signal,
      },
    });
    return await Promise.race([responsePromise, timeoutPromise]) as { text?: string };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Canonical category enum — duplicated as a const array here so we can
 * feed it into the response schema's `enum` field. Keep in lockstep
 * with `ClaimCategory` in `types/siteContext.ts`.
 */
const CLAIM_CATEGORIES = [
  'power.grid', 'power.btm', 'power.timeline',
  'water.cooling', 'water.source', 'water.rights',
  'environmental.species', 'environmental.wetlands', 'environmental.nepa',
  'environmental.tribal', 'environmental.phase1',
  'regulatory.zoning', 'regulatory.permits', 'regulatory.air',
  'geotechnical.foundation', 'geotechnical.seismic',
  'parcel.boundaries', 'parcel.acres',
  'connectivity.fiber', 'connectivity.latency',
  'schedule.inservice', 'schedule.permitting',
  'finance.cost', 'finance.ppa',
  'technology.reactor', 'technology.cooling', 'technology.workload',
  'prior-study', 'doc-reference', 'commitment', 'unverifiable',
] as const;

/**
 * Allow-list of SED dot-paths the model may emit on a claim. Anything
 * outside this set is dropped at validation. Mirrors the comparator
 * map in SC4; if you add a comparator, add its sedField here.
 */
const ALLOWED_SED_FIELDS = new Set<string>([
  'grid.substationDistKm',
  'grid.capacity',
  'grid.estimatedQueueTime',
  'grid.utilityName',
  'environmental.species',
  'environmental.hasWetlands',
  'environmental.hasCriticalHabitat',
  'environmental.wetlandAcres',
  'waterStress.stressLevel',
  'waterStress.bwsLabel',
  'parcel.acres',
  'parcel.zoning',
  'parcel.landUse',
  'regulatory.nuclearStance',
  'regulatory.state',
  'seismic.designCategory',
  'flood.floodZone',
  'flood.sfha',
  'climate.peakWetBulb',
  'connectivity.nearestIxDistKm',
  'nuclearInfra.nearestReactorDistKm',
]);

const RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    claims: {
      type: Type.ARRAY,
      description: 'Typed claims extracted from the site context prose.',
      items: {
        type: Type.OBJECT,
        properties: {
          category: {
            type: Type.STRING,
            description: 'Claim category — must be one of the canonical CLAIM_CATEGORIES values.',
            enum: [...CLAIM_CATEGORIES],
          },
          sourceSpan: {
            type: Type.STRING,
            description: 'The 1-2 sentence verbatim excerpt of the input prose this claim came from. ≤ 280 chars.',
          },
          claim: {
            type: Type.STRING,
            description: 'Short normalised bullet — ≤120 chars. Imperative voice, no hedging.',
          },
          claimedValue: {
            type: Type.STRING,
            description: 'Stringified value if the claim asserts a specific value (e.g. "187", "Q4 2026", "true"). Empty otherwise.',
          },
          sedField: {
            type: Type.STRING,
            description: 'SED dot-path (e.g. "grid.substationDistKm"), or empty when the claim is not verifiable.',
          },
          extractorConfidence: {
            type: Type.NUMBER,
            description: 'Confidence 0-100 that the claim was correctly parsed from the input.',
          },
        },
        required: ['category', 'sourceSpan', 'claim', 'extractorConfidence'],
      },
    },
  },
  required: ['claims'],
};

/**
 * Coerce a raw `claimedValue` string the model emitted into a typed
 * value when possible. Numeric strings become numbers, `'true'` /
 * `'false'` become booleans, everything else stays a string. Returns
 * `undefined` when the value was empty.
 */
function coerceClaimedValue(raw: string | undefined): string | number | boolean | undefined {
  if (raw === undefined || raw === null) return undefined;
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return undefined;
  if (/^true$/i.test(trimmed)) return true;
  if (/^false$/i.test(trimmed)) return false;
  // Pure numeric (integer or decimal), no units — let the comparator
  // decide on unit-bearing strings like "187 acres".
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return trimmed;
}

/**
 * SCE-FOLLOWUP-PROBABILISTIC-DISAMBIGUATION (SX-2) — confidence band
 * below which the extractor result is "low confidence" for the
 * disambiguation chip. The pack spec is `<0.5` on a 0-1 scale; the
 * extractor's `extractorConfidence` is on a 0-100 scale (see
 * `coerceClaimedValue` + the schema's `enum` constraint) so the
 * 0-100 threshold is 50.
 *
 * Exported so the regression suite can pin the threshold without
 * duplicating the magic number.
 */
export const SCE_LOW_CONFIDENCE_THRESHOLD = 50;

/* ── SCV (Site-Context Value-sanity) ──────────────────────────────────
 *
 * Three deterministic, IO-free post-passes run on the LLM output BEFORE
 * the claims[] return. They close the gaps the LLM-trusting coercion +
 * the SED-only validateClaim leave open for CATEGORY-LEVEL claims (claims
 * whose sedField is empty, which bypass validateClaim's range / unit /
 * polarity rules entirely):
 *
 *   (A) sanitiseClaimedValue — per-ClaimCategory plausible-range table +
 *       a "value must appear in the source span" guard + a "strip the
 *       number entirely for ownership/qualitative categories" rule. On a
 *       violation we DROP the numeric value (never guess a replacement)
 *       unless the span carries a single in-range figure we can re-bind.
 *   (B) detectNegationPolarity — for boolean claims, reads "no" / "not" /
 *       "not yet" / "has not" / "never" in the span so a "NOT initiated" /
 *       "NO permit filed" claim resolves to the correct polarity instead
 *       of whatever the LLM guessed.
 *   (C) collapseDuplicateClaims — keyed on category + normalised text +
 *       normalised value; keeps the row with a defined+in-range value
 *       over a bad one, collapsing exact dups.
 *
 * All three are pure, exported, and unit-tested. NONE hardcode any
 * site-specific (e.g. "Redhead") value — every threshold is generic.
 */

/**
 * SCV — per-claim numeric plausibility envelopes keyed by ClaimCategory.
 * These mirror the SED-path ranges in `fieldSchema.ts` but cover the
 * CATEGORY level (where there is no sedField and so validateClaim never
 * runs). `min`/`max` are inclusive. Categories absent from the table have
 * no numeric envelope and are not range-checked here (the value still
 * gets the value-must-appear-in-span guard).
 *
 * `subFields` is consulted when the claim/sourceSpan text contains the
 * sub-field keyword (e.g. "load factor"): the sub-field envelope OVERRIDES
 * the category envelope. This lets `power.grid` carry both raw figures and
 * a load-factor sub-claim (0-100 as a percentage, 0-1 as a fraction).
 */
interface ScvRange {
  readonly min: number;
  readonly max: number;
}
interface ScvCategoryRule {
  /** Category-wide envelope (optional — some categories only sub-field-check). */
  readonly range?: ScvRange;
  /**
   * Keyword → envelope. The FIRST keyword (case-insensitive, substring)
   * found in the claim or sourceSpan text wins and overrides `range`.
   */
  readonly subFields?: ReadonlyArray<{ readonly keywords: readonly string[]; readonly range: ScvRange }>;
}

const SCV_CATEGORY_RULES: Readonly<Partial<Record<ClaimCategory, ScvCategoryRule>>> = Object.freeze({
  'power.grid': {
    subFields: [
      // Load factor / capacity factor / utilisation: a percentage in
      // [0, 100] or a fraction in [0, 1]. Anything else (e.g. a stray
      // "5.5" MVAR figure) is rejected for a load-factor claim.
      {
        keywords: ['load factor', 'capacity factor', 'utilization', 'utilisation', 'power factor', 'load-factor'],
        range: { min: 0, max: 100 },
      },
    ],
  },
  'parcel.acres': { range: { min: 0.1, max: 100_000 } },
  // Distance-style connectivity (km). connectivity.fiber is frequently an
  // OWNERSHIP claim with no real distance — see SCV_QUALITATIVE_CATEGORIES.
  'connectivity.latency': { range: { min: 0, max: 1000 } },
});

/**
 * SCV — categories that are intrinsically OWNERSHIP / QUALITATIVE: a
 * numeric value is almost never the point of the claim, so we strip any
 * numeric `claimedValue` UNLESS the source span carries a unit token that
 * signals a genuine numeric intent (and even then only if the figure is
 * actually present in the span). E.g. "Level 3, Zayo, Crown Castle own
 * fiber in the corridor" → ownership, no number; but "fiber is ~5 mi
 * away" → a distance the user explicitly stated.
 */
const SCV_QUALITATIVE_CATEGORIES: ReadonlySet<ClaimCategory> = new Set<ClaimCategory>([
  'connectivity.fiber',
]);

/**
 * SCV — unit tokens that signal a genuine numeric intent in a span. Used
 * by the qualitative-category guard: a number in a qualitative claim is
 * only kept when one of these tokens co-occurs in the span (i.e. the user
 * really did state a measurable quantity). Generic across distance / area
 * / power / time so it is not site-specific.
 */
const SCV_NUMERIC_INTENT_TOKENS: readonly string[] = [
  'km', 'kilometer', 'kilometers', 'kilometre', 'kilometres',
  'mi', 'mile', 'miles',
  'ft', 'feet', 'foot', 'm', 'meter', 'meters', 'metre', 'metres',
  'acre', 'acres', 'ac',
  'mw', 'kw', 'gw', 'mva', 'kv', 'mvar',
  'ms', 'msec', 'millisecond', 'milliseconds',
  'month', 'months', 'year', 'years', 'week', 'weeks', 'day', 'days',
  '%', 'percent',
];

/**
 * SCV — does a numeric value actually appear as a token in the span?
 *
 * Reuses `extractNumericTokens` so the matching is identical to the rest
 * of the pipeline. We compare with a small relative tolerance (1e-9 or
 * 1e-6·|value|) so floating-point round-trips of the SAME stated figure
 * match, but a geometry-derived float (e.g. 332.41618384225995 when the
 * span says "800") does NOT.
 *
 * Pure / synchronous.
 */
export function valueAppearsInSpan(value: number, span: string): boolean {
  if (!Number.isFinite(value) || !span) return false;
  const tokens = extractNumericTokens(span);
  const tol = Math.max(1e-9, Math.abs(value) * 1e-6);
  for (const t of tokens) {
    if (Math.abs(t - value) <= tol) return true;
  }
  return false;
}

/**
 * SCV (B) — deterministic negation-polarity detector for BOOLEAN claims.
 *
 * EX-6: the implementation was lifted into the shared, dependency-light
 * module `src/lib/sed/negationPolarity.ts` so the DOC channel
 * (`extractFromDocument.ts`) can apply the SAME conservative polarity
 * flip without importing this heavy extractor module. It is imported
 * (top of file) for internal use and re-exported here so this file's
 * public surface + the regression suite's `import { detectNegationPolarity }`
 * from here keep working byte-for-byte.
 */
export { detectNegationPolarity };

/**
 * SCV (A) — apply the per-category value-sanity rules to a single claim's
 * coerced value. Returns the sanitised value (possibly `undefined` when
 * the value was dropped). Pure / synchronous.
 *
 * Rules, in order:
 *   1. Non-numeric values pass through unchanged (booleans handled by the
 *      negation pass; strings have no envelope).
 *   2. Qualitative/ownership categories: strip the number unless a unit
 *      token signals genuine numeric intent AND the figure is in the span.
 *   3. Range check (sub-field-aware): a number outside the category /
 *      sub-field envelope is dropped (we do NOT guess a replacement).
 *   4. Value-must-appear-in-span: a number that does not occur as a token
 *      in the source span is dropped UNLESS the span is empty (defensive)
 *      — this forbids geometry/area-derived floats the model invented.
 *      When the span carries exactly ONE in-range numeric, we re-bind to
 *      it (recovers the stated 800 when the model emitted a derived 332.4).
 */
export function sanitiseClaimedValue(
  value: string | number | boolean | undefined,
  category: ClaimCategory,
  claimText: string,
  sourceSpan: string,
): string | number | boolean | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;

  const haystack = `${claimText} ${sourceSpan}`;

  // 2. Ownership / qualitative categories — number only survives with a
  //    unit-token intent signal AND in-span presence.
  if (SCV_QUALITATIVE_CATEGORIES.has(category)) {
    const hasNumericIntent = sourceSpanContainsAnyToken(sourceSpan, SCV_NUMERIC_INTENT_TOKENS);
    if (!hasNumericIntent || !valueAppearsInSpan(value, sourceSpan)) {
      return undefined; // strip — the claim is about ownership, not a number
    }
    return value;
  }

  // 3. Range check (sub-field overrides category). When a sub-field
  //    keyword matches, remember it so re-bind can prefer the figure
  //    adjacent to that keyword (the percentage next to "load factor").
  const rule = SCV_CATEGORY_RULES[category];
  let range: ScvRange | undefined = rule?.range;
  let activeKeywords: readonly string[] | undefined;
  if (rule?.subFields) {
    const lower = haystack.toLowerCase();
    for (const sf of rule.subFields) {
      if (sf.keywords.some(k => lower.includes(k))) {
        range = sf.range;
        activeKeywords = sf.keywords;
        break;
      }
    }
  }
  if (range && (value < range.min || value > range.max)) {
    // Out of range — try to re-bind to an in-span figure before giving up.
    const rebind = rebindFromSpan(sourceSpan, range, activeKeywords);
    return rebind ?? undefined;
  }

  // 4. Value-must-appear-in-span (only when we have a span to check).
  if (sourceSpan && !valueAppearsInSpan(value, sourceSpan)) {
    const rebind = rebindFromSpan(sourceSpan, range, activeKeywords);
    return rebind ?? undefined;
  }

  return value;
}

/**
 * SCV — recover an in-span numeric for a claim whose model value was out
 * of range / absent from the span.
 *
 * Resolution order:
 *   1. If `keywords` is set and a keyword occurs in the span, return the
 *      in-range numeric token NEAREST the keyword (handles "load factor is
 *      83.3% ... 1.5 MW ... 33.3 MVAR" → 83.3, the figure adjacent to the
 *      keyword, not a stray nearby figure).
 *   2. Otherwise, return the single in-range token when the span carries
 *      EXACTLY ONE candidate (recovers the stated 800 over a derived float).
 *   3. Otherwise `undefined` — we never guess among multiple candidates.
 *
 * `range` is optional; when omitted, every numeric token is a candidate.
 * Pure / synchronous.
 */
function rebindFromSpan(
  span: string,
  range: ScvRange | undefined,
  keywords?: readonly string[],
): number | undefined {
  if (!span) return undefined;
  const inRangeOf = (t: number) => !range || (t >= range.min && t <= range.max);

  // 1. Keyword-adjacency: find the in-range numeric closest to a keyword.
  if (keywords && keywords.length > 0) {
    const lower = span.toLowerCase();
    let kwPos = -1;
    for (const kw of keywords) {
      const p = lower.indexOf(kw.toLowerCase());
      if (p >= 0 && (kwPos < 0 || p < kwPos)) kwPos = p;
    }
    if (kwPos >= 0) {
      const re = /(-?\d+(?:\.\d+)?)/g;
      let m: RegExpExecArray | null;
      let best: { val: number; dist: number } | undefined;
      while ((m = re.exec(span)) !== null) {
        const val = Number(m[1]);
        if (!Number.isFinite(val) || !inRangeOf(val)) continue;
        const dist = Math.abs(m.index - kwPos);
        if (!best || dist < best.dist) best = { val, dist };
      }
      if (best) return best.val;
    }
  }

  // 2. Single in-range candidate.
  const tokens = extractNumericTokens(span).filter(inRangeOf);
  const distinct = Array.from(new Set(tokens));
  return distinct.length === 1 ? distinct[0] : undefined;
}

/**
 * SCV — stop-words / hedge-words dropped from the dedup key so two
 * phrasings of the same fact collapse ("TGP operated by KM" vs "TGP IS
 * operated by KM"; "interconnect ~12 months FROM NTP" vs "interconnect
 * 12-month DURATION"). Intentionally generic English connective tissue —
 * NOT site-specific terms.
 */
const SCV_DEDUP_STOPWORDS: ReadonlySet<string> = new Set<string>([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'of', 'to', 'for', 'from', 'in', 'on', 'at', 'by', 'with', 'and', 'or',
  'approximately', 'approx', 'about', 'around', 'roughly', 'circa',
  'duration', 'confirmed', 'reportedly', 'per', 'as', 'that', 'this',
]);

/**
 * SCV — normalise a string for the dedup key: lowercase, drop punctuation
 * and the "~" hedge, tokenise, drop stop-words, singularise trailing
 * plural 's', and join number+unit tokens canonically. Pure.
 *
 * The aim is that two NEAR-identical phrasings of the SAME fact map to the
 * same key while genuinely distinct facts stay distinct. We are
 * conservative: only generic connective stop-words are dropped, so we do
 * not over-collapse (e.g. "MISO" and "BECI" still differ).
 */
function normaliseForKey(s: string | undefined): string {
  if (!s) return '';
  const cleaned = s
    .toLowerCase()
    .replace(/[’']/g, '')
    // "12-month" / "12month" → "12 month" so the number+unit normalises.
    .replace(/(\d)\s*[-]?\s*([a-z])/g, '$1 $2')
    .replace(/[^a-z0-9.]+/g, ' ');
  const tokens = cleaned
    .split(/\s+/)
    .filter(Boolean)
    .filter(tok => !SCV_DEDUP_STOPWORDS.has(tok))
    .map(tok => {
      // Singularise a trailing plural 's' on alpha tokens (months→month).
      if (/^[a-z]{3,}s$/.test(tok) && !tok.endsWith('ss')) return tok.slice(0, -1);
      return tok;
    });
  return tokens.join(' ').trim();
}

/**
 * SCV — normalise a coerced value for the dedup key. Numbers stringify
 * canonically; booleans → 'true'/'false'; strings normalise like text;
 * undefined → ''. Pure.
 */
function normaliseValueForKey(v: string | number | boolean | undefined): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return normaliseForKey(String(v));
}

/**
 * SCV (C) — collapse duplicate claims. Two claims are duplicates when they
 * share the same category AND the same normalised text (claim, falling
 * back to sourceSpan). Among a duplicate group we keep the BEST row:
 *   1. Prefer a row whose claimedValue is defined over one that is
 *      undefined (a value-bearing row beats a stripped one) — UNLESS the
 *      defined value was already sanitised away upstream, in which case
 *      both are undefined and tie-breaking falls through.
 *   2. Then prefer higher extractorConfidence.
 *   3. Then prefer the longer (more complete) sourceSpan.
 * Order-stable: the FIRST occurrence's position is preserved.
 *
 * Note: this runs AFTER sanitiseClaimedValue, so the "good 800 vs bad
 * 332.4" case is already a "defined 800 vs undefined" race that rule 1
 * resolves correctly. Pure / synchronous. Exported for the suite.
 */
export function collapseDuplicateClaims(claims: ExtractedClaim[]): ExtractedClaim[] {
  const groups = new Map<string, { idx: number; claim: ExtractedClaim }>();
  const order: string[] = [];
  for (let idx = 0; idx < claims.length; idx++) {
    const claim = claims[idx]!;
    const textKey = normaliseForKey(claim.claim) || normaliseForKey(claim.sourceSpan);
    const key = `${claim.category}::${textKey}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { idx, claim });
      order.push(key);
      continue;
    }
    if (preferClaim(claim, existing.claim)) {
      groups.set(key, { idx: existing.idx, claim });
    }
  }
  return order.map(k => groups.get(k)!.claim);
}

/**
 * SCV — true when `candidate` should replace `incumbent` in a dedup group.
 * Pure. See `collapseDuplicateClaims` for the precedence order.
 */
function preferClaim(candidate: ExtractedClaim, incumbent: ExtractedClaim): boolean {
  const candHasValue = candidate.claimedValue !== undefined;
  const incHasValue = incumbent.claimedValue !== undefined;
  if (candHasValue !== incHasValue) return candHasValue;
  if (candidate.extractorConfidence !== incumbent.extractorConfidence) {
    return candidate.extractorConfidence > incumbent.extractorConfidence;
  }
  return (candidate.sourceSpan?.length ?? 0) > (incumbent.sourceSpan?.length ?? 0);
}

/**
 * Numeric token detector. Matches standalone integer/decimal numerics
 * (no internal commas) up to one decimal place. Pure / synchronous.
 *
 * We do not match thousands-separator forms (`12,345`) because they
 * are vanishingly rare in the kind of site-context prose we ingest
 * and the regex stays simpler without them.
 */
function extractNumericTokens(text: string): readonly number[] {
  if (!text) return [];
  const out: number[] = [];
  // Word-boundary on the leading side; trailing must be non-digit so
  // "10km" matches "10". Negative sign optional.
  const re = /(?:^|[^0-9.])(-?\d+(?:\.\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = Number(m[1]);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

/**
 * SCE-FOLLOWUP-PROBABILISTIC-DISAMBIGUATION (SX-2) — heuristic detector
 * for "the source narrative carried multiple plausible candidates for
 * this claim".
 *
 * Strategy (intentionally narrow — false positives erode the chip's
 * signal):
 *   - String values: we require the value itself AND ≥1 other matching
 *     enum / state-code-shaped token to appear in the full text. Today
 *     that mainly covers the `regulatory.state` family (the only
 *     enum-typed schema we have); the namespace stays open for
 *     future enum fields.
 *   - Numeric values: we extract every numeric token from the full
 *     narrative and look for ≥2 distinct numerics within ±20 % of the
 *     extracted value. Two readings of "240" / "260" acres in the same
 *     paragraph is the canonical case.
 *   - Boolean / empty values: never flagged.
 *
 * Pure / synchronous. Always returns a boolean.
 */
export function detectAmbiguousValue(
  value: string | number | boolean | undefined,
  fullText: string,
): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return false;
  if (!fullText || fullText.length === 0) return false;

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value === 0) return false;
    const tokens = extractNumericTokens(fullText);
    if (tokens.length < 2) return false;
    // Count tokens within ±20 % of `value` that are NOT exactly equal
    // to the extracted value. ≥2 distinct neighbours ⇒ ambiguous.
    const tolerance = Math.abs(value) * 0.2;
    const distinctNeighbours = new Set<number>();
    for (const t of tokens) {
      if (t === value) continue;
      if (Math.abs(t - value) <= tolerance) distinctNeighbours.add(t);
    }
    return distinctNeighbours.size >= 1; // value + ≥1 neighbour = ambiguous
  }

  // String values: look for ≥1 other state-code / capitalised token
  // matching the same shape. We use a narrow regex for two-letter
  // uppercase codes (the dominant enum-typed string field today) AND
  // capitalised state names — anything else falls through to "not
  // ambiguous" so we don't over-flag plain prose.
  const v = String(value).trim();
  if (v.length === 0) return false;
  // Cheap normalisation — uppercase the value's leading chars for the
  // state-code branch. Full-name detection is handled by a wider scan.
  const stateCodeRe = /\b([A-Z]{2})\b/g;
  const seenCodes = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = stateCodeRe.exec(fullText)) !== null) {
    seenCodes.add(m[1] as string);
  }
  if (seenCodes.size >= 2 && seenCodes.has(v.toUpperCase())) return true;
  return false;
}

/**
 * Stable id generator — UUID-shaped enough for React keys + audit. Avoids
 * importing crypto for environments where it's unavailable at module load
 * (the store action runs in browser + node).
 */
function newClaimId(): string {
  // crypto.randomUUID is available in node 16+ and modern browsers.
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return `claim-${(crypto as Crypto).randomUUID()}`;
  }
  return `claim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Build the prompt body. Kept short — Flash is sensitive to context
 * length and we want sub-2s round trips on every wizard blur.
 */
function buildExtractorPrompt(text: string, options?: ExtractClaimsOptions): string {
  const siteRef = options?.siteName ? ` for the site "${options.siteName}"` : '';
  const stateHint = options?.siteStateCode
    ? `Site state (canonical): ${options.siteStateCode}. Do NOT populate regulatory.state with any OTHER state code unless the source span explicitly says the site is located in that other state.`
    : '';
  // SCE-FOLLOWUP-AUTO-RE-EXTRACT-ON-MISMATCH (SX-1) — strengthened
  // prefix appended when this is a retry pass. Lists the rejected
  // proposals + their failure reasons so the LLM can avoid repeating
  // them. Capped to 5 rejection rows so the prompt stays small even
  // on a pathological first pass.
  const retryPrefix = options?.retryContext
    ? buildRetryPrefix(options.retryContext)
    : '';
  return [
    retryPrefix,
    'You are extracting AUTHORITATIVE FACTS from a site owner\'s description' + siteRef + '.',
    '',
    'Return a JSON object { "claims": [...] }. For each claim:',
    `- category: one of the canonical enum values (${[...CLAIM_CATEGORIES].slice(0, 8).join(', ')}, ...).`,
    '- sourceSpan: the 1-2 sentence verbatim excerpt from the input.',
    '- claim: short normalised bullet (≤120 chars).',
    '- claimedValue: stringified value if the claim asserts one (e.g. "187", "Q4 2026", "true"). Omit/empty otherwise.',
    '- sedField: dot-path the claim maps to (one of: ' + [...ALLOWED_SED_FIELDS].join(', ') + '). Empty when unverifiable.',
    '- extractorConfidence: 0-100.',
    '',
    'TYPE DISCIPLINE — read this before populating sedField:',
    '- A numeric sedField value (e.g. grid.substationDistKm in km, parcel.acres in acres,',
    '  connectivity.nearestIxDistKm in km, nuclearInfra.nearestReactorDistKm in km,',
    '  environmental.wetlandAcres in acres) requires the source span to EXPLICITLY contain a',
    '  unit token matching the field. "300 MW BTM" is NOT a valid source for grid.substationDistKm',
    '  because the span carries "MW" (power), not "km" (distance). If you cannot find a unit',
    '  token matching the field in the span, leave sedField empty (the claim still ships as',
    '  free-form prose into the LLM prompt block, it just does not bind to a typed SED slot).',
    '- regulatory.state must be a US state. If the site is in one state and the narrative',
    '  mentions another state in passing (e.g. a pipeline easement named after a different',
    '  state, a parent-company HQ elsewhere), do NOT populate regulatory.state with that other',
    '  state unless the span carries an explicit locus phrase such as "the site is located in X"',
    '  or "<site-name> in X".',
    stateHint,
    '- Plausibility envelopes apply: grid.substationDistKm in [0, 100] km; parcel.acres in',
    '  [0.1, 100000]; connectivity.nearestIxDistKm in [0, 2000] km; reactor distance [0, 5000] km.',
    '  A value far outside an envelope is almost always a type/unit error — leave sedField empty.',
    '',
    'VALUE DISCIPLINE — read this before populating claimedValue:',
    '- claimedValue MUST be a figure that appears VERBATIM in the source span. Never put a',
    '  number that is not literally written in the span (do NOT derive acreage from geometry,',
    '  do NOT grab a nearby unrelated figure). If "800 acres" is stated, claimedValue is "800".',
    '- For a "load factor" / "capacity factor" claim, claimedValue is the percentage itself',
    '  (e.g. "83.3" for "load factor is 83.3%"), NOT a different number from the same sentence.',
    '- NEGATION: if the span says a thing is NOT done / has NO permit / is NOT YET initiated,',
    '  the boolean for a claim asserting that negated state is "true" (the negated fact holds).',
    '  e.g. "permitting is not yet initiated" → a "not-initiated" claim has claimedValue "true".',
    '- OWNERSHIP / qualitative claims (e.g. "X, Y own fiber in the corridor") assert no number —',
    '  leave claimedValue empty. Only emit a numeric value when the span states a measurable',
    '  quantity with a unit (e.g. "fiber is ~5 mi away").',
    '- Do NOT emit two claims for the same fact; state each fact once.',
    '',
    'DO NOT:',
    '- Fabricate claims not present in the text.',
    '- Emit sedField values outside the allow-list above.',
    '- Return claims with category=unverifiable when the text is clearly verifiable (timelines, acreage, species, etc.).',
    '- Include claims with extractorConfidence < 40.',
    '- Cross-type a value: if the narrative says "300 MW", that figure can map to a power',
    '  field but NOT to a distance field, acreage field, or cost field.',
    '',
    'INPUT:',
    text.slice(0, SITE_CONTEXT_MAX_CHARS),
  ].filter(Boolean).join('\n');
}

/**
 * SCE-FOLLOWUP-AUTO-RE-EXTRACT-ON-MISMATCH (SX-1) — strengthened prompt
 * prefix that lists the rejected proposals from a prior pass so the LLM
 * can avoid repeating them. Mirrors MEL-MR-1's
 * `STRENGTHENED_PROMPT_PREFIX` shape. Capped to 5 rejection rows so the
 * prompt stays bounded.
 *
 * Exported so the regression suite can pin the contract.
 */
export function buildRetryPrefix(ctx: SceRetryContext): string {
  const rows = ctx.priorRejections.slice(0, 5);
  if (rows.length === 0) {
    // Defensive — caller should never invoke a retry without
    // rejections, but if they do we still emit a generic admonishment.
    return [
      'CRITICAL: This is a RETRY. The previous extraction was rejected by ',
      'the post-validation schema. Re-read the source carefully; if a ',
      'numeric field has no unit token in its span, LEAVE sedField EMPTY.',
      '',
    ].join('');
  }
  const lines: string[] = [
    'CRITICAL: This is a RETRY. The previous extraction proposed claims ',
    'that the post-validation schema REJECTED. Do NOT re-propose these ',
    'same (sedField, value) pairs — they are wrong. The rejections were:',
    '',
  ];
  for (const row of rows) {
    const field = row.proposedSedField ?? '<no sedField>';
    const value = row.proposedValue !== undefined ? String(row.proposedValue) : '<no value>';
    const reasons = row.reasons.map(r => r.code).join(', ');
    const span = row.sourceSpan ? ` (span: "${row.sourceSpan.slice(0, 100)}")` : '';
    lines.push(`- ${field} = ${value} → rejected for: ${reasons}${span}`);
  }
  lines.push(
    '',
    'Common fixes: leave sedField EMPTY when the source span lacks an ',
    'explicit unit token; never put an MW / acres / cost figure into a ',
    'distance (km) field; never put a non-locus state mention into ',
    'regulatory.state. Confidence should reflect remaining uncertainty.',
    '',
  );
  return lines.join('\n');
}

/**
 * SCE-FOLLOWUP-AUTO-RE-EXTRACT-ON-MISMATCH (SX-1) — orchestrator that
 * runs `extractClaims` and, when the first pass produced rejected
 * claims, fires EXACTLY ONE retry with a strengthened prompt prefix.
 *
 * Bounding rules (mirror MR-1):
 *   1. At most ONE retry per call. There is no recursion / no
 *      second-pass retry. If the retry also produces rejections, those
 *      rejections stay rejected.
 *   2. The retry result fully REPLACES the first pass's result so
 *      callers don't get a Frankenstein merge of two extractor runs.
 *      Rationale: if the retry surfaced new, cleaner claims, they
 *      already supersede the first pass; if the retry came back empty
 *      / errored, returning that result preserves the contract that
 *      `status` reflects the latest attempt.
 *   3. Atomicity: a retry call that THROWS gracefully degrades — the
 *      first pass's result is returned unchanged. We never bubble the
 *      retry error to the caller (consistent with MR-1's atomicity
 *      contract).
 *
 * Pre-conditions:
 *   - Identical contract to `extractClaims`; safe to drop in as a
 *     replacement at every call site that wants the retry behaviour.
 *
 * Cost envelope: at most 2× the cost of a single `extractClaims` call.
 */
export async function extractClaimsWithRetry(
  text: string,
  options?: ExtractClaimsOptions,
): Promise<ExtractClaimsResult> {
  // Defensive: callers SHOULD NOT pass `retryContext` themselves —
  // strip it so the first pass is always a fresh extraction.
  const baseOptions: ExtractClaimsOptions | undefined = options
    ? { ...options, retryContext: undefined }
    : undefined;
  const first = await extractClaims(text, baseOptions);

  // Only retry when the first pass actually produced rejected rows.
  // Errors / empty success / quota all leave the first result intact.
  if (first.status !== 'success') return first;
  if (!first.rejected || first.rejected.length === 0) return first;

  let second: ExtractClaimsResult;
  try {
    second = await extractClaims(text, {
      ...(baseOptions ?? {}),
      retryContext: {
        isRetry: true,
        priorRejections: first.rejected.map(r => ({
          proposedSedField: r.proposedSedField,
          proposedValue: r.proposedValue,
          sourceSpan: r.sourceSpan,
          reasons: r.reasons,
        })),
      },
    });
  } catch (e) {
    // MR-1 atomicity rule. A thrown retry NEVER fails the parent —
    // we return the first-pass result so the caller still sees the
    // initial claims + rejections.
    const reason = (e as Error)?.message ?? String(e);
    console.warn(`[site-context] extractClaimsWithRetry: retry threw — keeping first-pass result (${reason})`);
    return first;
  }

  // Atomicity guard: if the retry call errored at the API boundary
  // (parse-error / quota-error / etc.), prefer the first pass — at
  // least it had structured output. Only adopt the retry when it
  // reached `success`.
  if (second.status !== 'success') return first;
  return second;
}

/**
 * Best-effort claim extraction. Errors return `{ claims: [], extractorVersion }`
 * so the caller can persist the raw prose without surfacing a failure to
 * the user — the LLM still gets the text via buildSiteContextBlock.
 */
export async function extractClaims(
  text: string,
  options?: ExtractClaimsOptions,
): Promise<ExtractClaimsResult> {
  const attemptedAt = Date.now();
  const trimmed = (text ?? '').trim();
  if (trimmed.length === 0) {
    // CLP-1 — empty input is a "success-with-nothing-to-do", not an
    // error. Status 'success' so the chip reads `0 claims parsed` and
    // the retry UI stays hidden.
    return { claims: [], extractorVersion: EXTRACTOR_VERSION, status: 'success', attemptedAt };
  }

  try {
    const prompt = buildExtractorPrompt(trimmed, options);
    const timeoutMs = computeExtractorTimeoutMs(trimmed.length);
    let response: { text?: string } | undefined;

    for (let attempt = 1; attempt <= EXTRACTOR_TIMEOUT_MAX_ATTEMPTS; attempt++) {
      try {
        response = await generateClaimsWithTimeout(prompt, timeoutMs);
        break;
      } catch (attemptErr) {
        const attemptStatus = classifyExtractorError(attemptErr);
        if (attemptStatus === 'timeout' && attempt < EXTRACTOR_TIMEOUT_MAX_ATTEMPTS) {
          console.warn(
            `[site-context] extractClaims attempt ${attempt}/${EXTRACTOR_TIMEOUT_MAX_ATTEMPTS} timed out after ${timeoutMs}ms; retrying`,
            {
              inputChars: trimmed.length,
              promptChars: prompt.length,
              estimatedInputTokens: estimateTokens(trimmed),
              estimatedPromptTokens: estimateTokens(prompt),
            },
          );
          await sleep(retryBackoffMs(attempt));
          continue;
        }
        throw attemptErr;
      }
    }

    if (!response) {
      throw new Error(`extractClaims timed out after ${timeoutMs}ms`);
    }

    const raw = (response as { text?: string }).text ?? '';
    if (!raw) {
      // CLP-1 — empty response from a model that didn't throw. Classify
      // as parse-error so the retry affordance surfaces; the model's
      // schema response should never be the empty string on a healthy
      // response.
      return {
        claims: [],
        extractorVersion: 'extractor-error',
        status: 'parse-error',
        errorMessage: 'Model returned an empty response',
        attemptedAt,
      };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (parseErr) {
      const msg = (parseErr as Error)?.message ?? 'Unknown JSON parse error';
      console.warn('[site-context] extractClaims: response was not valid JSON');
      return {
        claims: [],
        extractorVersion: 'extractor-error',
        status: 'parse-error',
        errorMessage: msg,
        attemptedAt,
      };
    }

    const rawClaims = Array.isArray((parsed as { claims?: unknown }).claims)
      ? ((parsed as { claims: unknown[] }).claims)
      : [];

    const claims: ExtractedClaim[] = [];
    const rejected: SceRejectedClaim[] = [];
    for (const c of rawClaims) {
      if (!c || typeof c !== 'object') continue;
      const obj = c as Record<string, unknown>;
      const category = typeof obj.category === 'string' ? obj.category : '';
      if (!(CLAIM_CATEGORIES as readonly string[]).includes(category)) continue;
      const sourceSpan = typeof obj.sourceSpan === 'string' ? obj.sourceSpan.trim() : '';
      const claim = typeof obj.claim === 'string' ? obj.claim.trim() : '';
      if (!sourceSpan || !claim) continue;
      const sedFieldRaw = typeof obj.sedField === 'string' ? obj.sedField.trim() : '';
      const sedField = sedFieldRaw && ALLOWED_SED_FIELDS.has(sedFieldRaw) ? sedFieldRaw : undefined;
      const coerced = coerceClaimedValue(typeof obj.claimedValue === 'string' ? obj.claimedValue : undefined);
      // SCV (B) — negation polarity. For BOOLEAN claims, resolve the
      // polarity deterministically from the source span / claim text so a
      // "NOT yet initiated" / "NO permit filed" claim reads the way the
      // text says, not the way the LLM guessed. We flip ONLY when the text
      // carries an unambiguous negation marker (detectNegationPolarity
      // returns `true`); otherwise we keep the LLM's boolean.
      let claimedValue: string | number | boolean | undefined = coerced;
      if (typeof coerced === 'boolean') {
        const negated = detectNegationPolarity(`${claim} ${sourceSpan}`);
        if (negated === true) {
          // The text NEGATES the predicate. A claim asserting the
          // negated state ("workstream is NOT initiated", "NO permit
          // filed") resolves to TRUE for that claim.
          claimedValue = true;
        }
      } else {
        // SCV (A) — per-category numeric value-sanity (range + must-appear-
        // in-span + qualitative-strip). Applies to category-level claims
        // that bypass validateClaim entirely. Drops a wrong/area-derived
        // number rather than letting it poison the report LLM.
        claimedValue = sanitiseClaimedValue(coerced, category as ClaimCategory, claim, sourceSpan);
      }
      const confRaw = obj.extractorConfidence;
      const rawExtractorConfidence = typeof confRaw === 'number' && Number.isFinite(confRaw)
        ? Math.max(0, Math.min(100, confRaw))
        : 50;
      // CL-1 (row 0.99286) — apply per-category calibration to the raw
      // LLM confidence before any threshold or downstream consumer
      // reads it. All 32 multipliers default to 1.0 so this is a no-op
      // at ship time; live-prod telemetry can shift specific claim
      // types via the calibration table without changing the rest of
      // the pipeline.
      const extractorConfidence = calibrateClaimConfidence(
        category as ClaimCategory,
        rawExtractorConfidence,
      );
      if (extractorConfidence < 40) continue;
      // SCE-2 — post-validation gate. Runs only when `sedField` is set
      // (untyped narrative claims pass through; they are not committed
      // to a SED slot, so there's no type to violate). When the schema
      // rejects with `severity: 'block'`, the row goes into
      // `rejected[]` and never lands in `claims[]`. Warnings (warn
      // severity) attach `qualityWarnings` and the claim still ships.
      let qualityWarnings: SceQualityWarning[] = [];
      if (sedField) {
        const v = validateClaim({
          sedField,
          value: claimedValue,
          sourceSpan,
          sed: options?.sed ?? null,
          siteStateCode: options?.siteStateCode ?? null,
          siteName: options?.siteName,
        });
        if (!v.ok) {
          rejected.push({
            proposedCategory: category,
            proposedSedField: sedField,
            proposedValue: claimedValue,
            sourceSpan: sourceSpan.slice(0, 280),
            reasons: v.failures.map(f => ({ code: f.code, reason: f.reason })),
            extractorVersion: EXTRACTOR_VERSION,
          });
          continue;
        }
        qualityWarnings = [...v.qualityWarnings];
      }
      // SCE-FOLLOWUP-PROBABILISTIC-DISAMBIGUATION (SX-2). Independent
      // of SED-binding — fires on any low-confidence claim whose value
      // has a plausible alternate in the full narrative. Surfaced as a
      // chip via `SceQualityChip`; does NOT block the claim.
      if (
        extractorConfidence < SCE_LOW_CONFIDENCE_THRESHOLD &&
        claimedValue !== undefined &&
        detectAmbiguousValue(claimedValue, trimmed) &&
        !qualityWarnings.includes('sce-ambiguous')
      ) {
        qualityWarnings.push('sce-ambiguous');
      }
      claims.push({
        id: newClaimId(),
        category: category as ClaimCategory,
        sourceSpan: sourceSpan.slice(0, 280),
        claim: claim.slice(0, 120),
        claimedValue,
        sedField,
        extractorConfidence,
        ...(qualityWarnings.length > 0 ? { qualityWarnings } : {}),
      });
    }

    // SCV (C) — collapse intra-list duplicates (same category + normalised
    // text). Runs LAST so the value-sanity pass has already turned a bad
    // numeric row into a value-stripped row, letting the "prefer defined +
    // in-range value" rule keep the good row over the bad/duplicate one.
    const dedupedClaims = collapseDuplicateClaims(claims);

    return {
      claims: dedupedClaims,
      extractorVersion: EXTRACTOR_VERSION,
      status: 'success',
      attemptedAt,
      ...(rejected.length > 0 ? { rejected } : {}),
    };
  } catch (e) {
    const msg = (e as Error)?.message ?? String(e);
    const status = classifyExtractorError(e);
    if (status === 'timeout') {
      const prompt = buildExtractorPrompt(trimmed, options);
      logExtractClaimsFailure(status, msg, {
        inputText: trimmed,
        prompt,
        timeoutMs: computeExtractorTimeoutMs(trimmed.length),
        attempt: EXTRACTOR_TIMEOUT_MAX_ATTEMPTS,
      });
    } else {
      console.warn(`[site-context] extractClaims failed (${status}):`, msg);
    }
    return {
      claims: [],
      extractorVersion: 'extractor-error',
      status,
      errorMessage: msg,
      attemptedAt,
    };
  }
}

/**
 * Exposed for tests + comparators — the comparator file consults
 * ALLOWED_SED_FIELDS via this helper to guard `resolveSedField`.
 */
export function isAllowedSedField(field: string): boolean {
  return ALLOWED_SED_FIELDS.has(field);
}

/** Test-only export of the schema for the regression suite. */
export { RESPONSE_SCHEMA as __TEST_RESPONSE_SCHEMA };
